import { type FrontRoyalStore, type Image } from 'FrontRoyalStore';
import StorableImageError from './StorableImageError';

// A put of a whole lesson at once is one long task, which locks up the UI on iOS.
const PUT_BATCH_SIZE = 6;

// Streams and lessons both run one at a time, so this module-level pool bounds the whole pass.
const FETCH_CONCURRENCY = 6;

const waitingForSlot: (() => void)[] = [];
let slotsInUse = 0;

async function withFetchSlot<T>(fn: () => Promise<T>) {
    if (slotsInUse >= FETCH_CONCURRENCY) await new Promise<void>(resolve => waitingForSlot.push(resolve));
    else slotsInUse += 1;

    try {
        return await fn();
    } finally {
        const next = waitingForSlot.shift();

        // Hand the slot to the next waiter rather than freeing it, so a caller that arrives
        // before the waiter resumes cannot take the slot and push us over the limit.
        if (next) next();
        else slotsInUse -= 1;
    }
}

// Do not narrow this with `anyOf`. Reading every key is much faster on iOS.
export async function loadStoredImageUrls(frontRoyalStore: FrontRoyalStore) {
    const urls = await frontRoyalStore.retryOnHandledError(db => db.images.toCollection().primaryKeys());
    return new Set(urls);
}

export default class StoredImagesRepo {
    urls: string[];
    frontRoyalStore: FrontRoyalStore;
    imagesFetched: Image[] = [];
    urlsToRetry: string[] = [];
    // Shared across repos so that a url used by two lessons is only fetched once.
    claimedUrls: Set<string>;

    constructor(urls: string[], frontRoyalStore: FrontRoyalStore, claimedUrls: Set<string>) {
        this.urls = urls;
        this.frontRoyalStore = frontRoyalStore;
        this.claimedUrls = claimedUrls;
    }

    async fetchAndStoreImages() {
        await this.fetchImages(this.urls, 'retry');
        await this.storeFetchedImages();

        // The retry bypasses the http cache.
        // We added that after we hit a 502 cached for a year. See https://trello.com/c/QHzY0hMK.
        await this.fetchImages(this.urlsToRetry, 'throw', 'reload');
        await this.storeFetchedImages();
    }

    async fetchImages(urls: string[], onError?: string, cache?: RequestCache) {
        await Promise.all(urls.map(url => this.fetchArrayBuffer(url, onError, cache)));
    }

    async storeFetchedImages() {
        if (!this.imagesFetched.length) return;

        const images = this.imagesFetched;
        this.imagesFetched = [];

        // Originally, we tried to put images in bulk but sometimes we ran into
        // "Failed to write Data Blob" errors. We made that go away by putting the images
        // into the db one-by-one. Around 10/3/2022 we went back to the bulk put to speed things
        // up. If we hit the same error again, we should catch it and fall back to putting the images
        // one-by-one. (consider the promise-retry library)
        await this.frontRoyalStore.retryRequestOnHandledError(
            'storeImages',
            images,
            // See comment near similar line in getStreamAndEnsureStored
            { errorHandlingPolicies: { throwOnStorageQuotaExceededError: true } },
        );
    }

    async fetchArrayBuffer(url: string, onError = 'throw', cache: RequestCache = 'default') {
        if (this.claimedUrls.has(url)) return;

        // Claim before the await, not after the put. A url claimed only after its put lets a
        // second lesson start a duplicate fetch while the first one is still in flight.
        this.claimedUrls.add(url);

        const genericFailureMsg = `Failed to fetch image ${url}`;
        try {
            const data = await withFetchSlot(async () => {
                const response = await fetch(url, { cache });
                if (!response.ok) throw new Error(genericFailureMsg);
                return response.arrayBuffer();
            });
            this.imagesFetched.push({
                url,
                data,
            });
        } catch (error) {
            this.claimedUrls.delete(url);

            if (onError === 'retry') {
                this.urlsToRetry.push(url);
                return;
            }
            throw new StorableImageError((error instanceof Error && error.message) || genericFailureMsg, {
                cause: error,
            });
        }

        // storeFetchedImages swaps the buffer out before it awaits, so concurrent callers
        // here always get disjoint batches.
        if (this.imagesFetched.length >= PUT_BATCH_SIZE) await this.storeFetchedImages();
    }
}
